// This React component renders the UPS banned drivers table, honoring global agency/search filters,
// relying on bansSlice as the read model and centralized socket-driven invalidation,
// and letting admins unban drivers while routing details through DriverDetailsModal.

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import { upsertDriver } from '../drivers/driverSlice';

import { unbanDriver } from '../../services/upsDriverService';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverDetailsModal from './modals/DriverDetailsModal';

import {
  loadBannedDrivers,
  selectBannedRows,
  selectBansLoading,
  selectBansError,
} from '../bans/bansSlice';

export default function BannedDriversList() {
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsUpsAdmin);

  const { agencyId, q, source } = useSelector(selectFilters);
  const agencies = useSelector(selectAllAgencies);

  const bannedRows = useSelector(selectBannedRows);
  const loading = useSelector(selectBansLoading);
  const error = useSelector(selectBansError);

  // Details modal state
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetailsRow, setSelectedDetailsRow] = useState(null);

  useEffect(() => {
    dispatch(loadBannedDrivers());
  }, [dispatch, agencyId, q, source]);

  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  const handleUnban = async (row) => {
    try {
      const idForApi = row.ban_id ?? row.id;
      await unbanDriver(idForApi);
      dispatch(loadBannedDrivers());
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Unban failed:', err);
    }
  };

  const openDetails = (row) => {
    dispatch(upsertDriver(row));
    setSelectedDetailsRow(row);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedDetailsRow(null);
  };

  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Banned Drivers</h3>

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-100 p-3 rounded">
            {error}
          </div>
        )}

        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name', 'Vehicle Reg.', 'Phone', 'Email', 'Owner', 'Banned At', 'Actions'].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  Loading banned drivers…
                </td>
              </tr>
            ) : (bannedRows || []).length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No banned drivers{agencyId ? ` for ${scopeLabel}` : ''}
                  {hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : (
              (bannedRows || []).map((row) => (
                <tr key={row.ban_id ?? row.id} data-row-id={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.vehicle_registration_number || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{row.phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{row.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatOwnerLabel(row)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.banned_at ? new Date(row.banned_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleUnban(row)}
                        disabled={!isAdmin}
                        title={isAdmin ? 'Unban driver' : 'Admins only'}
                        className={
                          'px-2 py-1 rounded text-green-600 ' +
                          (isAdmin
                            ? 'hover:bg-gray-100'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                        }
                      >
                        <UserCheck size={16} />
                      </button>

                      <button
                        onClick={() => openDetails(row)}
                        className="px-2 py-1 rounded hover:bg-gray-100 text-blue-600"
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showDetails && selectedDetailsRow && (
        <DriverDetailsModal
          driver={selectedDetailsRow}
          onClose={closeDetails}
        />
      )}
    </>
  );
}